import React, { useEffect, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import StoreController, {
  CurrentStoreContext,
  HotelIntroType,
} from '../../../controller/StoreController';
import { useToast } from '../../../controller/ToastController';

const GuideIndex = () => {
  const showToast = useToast();

  const navigate = useNavigate();
  const [guideList, setGuideList] = useState<HotelIntroType[]>([]);

  const { currentStore } = useOutletContext<CurrentStoreContext>();

  const navigateGuideInfo = async (guideItem?: HotelIntroType) => {
    if (guideItem) {
      navigate(`/info/guide/edit/${guideItem.card_id}`);
    } else navigate(`/info/guide/edit/-1`);
  };

  const getHotelIntro = async () => {
    try {
      const oParams = {
        store_id: currentStore.value,
      };

      const oRes = await StoreController.getHotelIntro(oParams);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('getHotelIntro', oResponse);
        if (oResponse.data && oResponse.ok) {
          setGuideList(oResponse.data);
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  const editHotelIntroIdOrder = async () => {
    try {
      const oParams = {
        store_id: currentStore.value,
        hotel_intro: guideList,
      };

      const oRes = await StoreController.editHotelIntroIdOrder(oParams);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('editHotelIntroIdOrder', oResponse);
        if (oResponse.data && oResponse.ok) {
          showToast('호텔소개 순서를 변경했어요');
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 1) toIndex = 0;
    else if (toIndex > guideList.length - 1) toIndex = guideList.length;
    const newItems = [...guideList];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    setGuideList(newItems);
  };

  useEffect(() => {
    getHotelIntro();
  }, [currentStore]);

  interface DraggableItemProps {
    item: HotelIntroType;
    index: number;
    moveItem: (fromIndex: number, toIndex: number) => void;
  }

  const RenderGuideItem = ({ item, index, moveItem }: DraggableItemProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(index + 1);

    const [, drag] = useDrag({
      type: 'ITEM',
      item: { item, index },
    });

    const [, drop] = useDrop({
      accept: 'ITEM',
      hover: (item: { index: number }, monitor: DropTargetMonitor) => {
        if (item.index !== index) {
          moveItem(item.index, index);
          item.index = index;
        }
      },
    });

    return (
      <div key={index}>
        <div className={`listItem`}>
          <div className="row gap">
            <input
              ref={(node) => drag(drop(node))}
              value={selectedIndex}
              autoFocus={false}
              onChange={(e) => {
                setSelectedIndex(Number(e.target.value));
              }}
              onBlur={() => {
                moveItem(index, selectedIndex - 1);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  moveItem(index, selectedIndex - 1);
                }
              }}
            />
            <div className="imgWrap">
              {item.url !== null && item.url !== '' && <img src={item.url} alt="guide_logo" />}
            </div>
            <h3>{item.title}</h3>
          </div>
          <button className="editButton" onClick={() => navigateGuideInfo(item)}>
            수정하기
          </button>
        </div>
      </div>
    );
  };

  return (
    <Style>
      <div className="contentContainer">
        <h3 className="title">호텔 컨텐츠 관리</h3>
        <div className="warningSign">
          <img src={`${process.env.PUBLIC_URL}/svg/ic_warning.svg`} alt="ic" />
          <p>숫자를 입력하거나 숫자 버튼을 드래그 해서 순서를 변경해주세요.</p>
        </div>

        <DndProvider backend={HTML5Backend}>
          {guideList.length > 0 ? (
            <div className="categoryList">
              {guideList.map((item: HotelIntroType, index: number) => {
                return (
                  <RenderGuideItem key={index} item={item} index={index} moveItem={moveItem} />
                );
              })}
            </div>
          ) : (
            <div className="noneWrap">
              <h3>호텔소개를 등록해주세요</h3>
            </div>
          )}
        </DndProvider>
        <button className="addButton" onClick={() => navigateGuideInfo()}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_plus.svg`} alt="ic" />
          <h4>호텔소개 추가하기</h4>
        </button>
        <button className={`submitButton`} onClick={editHotelIntroIdOrder}>
          저장
        </button>
      </div>
    </Style>
  );
};

export default GuideIndex;

const Style = styled.div`
  h3.title {
    font-weight: 500;
    color: #555555;
    margin-bottom: 30px;
  }

  .addButton {
    width: 100%;
    border: 1px solid #4dd2ae;
    border-radius: 5px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5%;
    margin: 10px 0;

    h4 {
      font-weight: 600;
      color: #4dd2ae;
      white-space: nowrap;
      padding: 0;
    }
  }

  .categoryList {
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .listItem {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 40px 10px 20px;
      border: 1px solid #e5e5e5;
      border-radius: 5px;

      input {
        width: 30px;
        height: 30px;
        padding: 4px;
        font-size: 16px;
        line-height: 20px;
        font-weight: 600;
        text-align: center;
        color: #4dd2ae;
        background: #ffffff;
        border: 1px solid #4dd2ae;
        border-radius: 4px;
      }

      .row.gap {
        gap: 14px;
      }
      h3 {
        font-size: 18px;
        color: #555555;
        font-weight: 600;
      }

      .imgWrap {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        border-radius: 5px;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 5px;
        }
      }

      .editButton {
        background: #ffffff;
        text-align: center;
        border-radius: 5px;
        padding: 6px 14px;
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
        border: 1px solid #cecece;
        color: #979797;
        cursor: pointer;
      }
    }
  }

  @media (max-width: 600px) {
    .categoryList {
      gap: 20px;

      .listItem {
        padding: 8px 10px 8px 16px;

        input {
          width: 26px;
          height: 26px;
          font-size: 14px;
        }
        h3 {
          font-size: 16px;
          color: #555555;
          font-weight: 600;
        }
      }
    }
  }
`;
